import { describeOffer, describeRate, isRecurring } from '@affily/shared/commission';
import { formatCents, formatPercent, formatCount } from '@affily/shared/money';
import { formatDay } from '@affily/shared/programDates';
import {
  PAYOUT_SCHEDULE_LABELS,
  PAYOUT_SCHEDULE_DESCRIPTIONS,
  COMMISSION_TYPE_LABELS,
  GIFT_FULFILMENT_LABELS,
} from '@affily/shared/constants';
import { Unknown } from './ui.jsx';

/**
 * The public terms block.
 *
 * One component, used on the program page, in the directory row, in the
 * application flow and on the partnership record. There is no second
 * "preview" rendering that could drift from what actually gets paid — and no
 * prop that hides a value from some viewers.
 */

/**
 * What a creator gets besides (or instead of) money. The retail value is the
 * brand's own figure and says so; it is simply left out when none was given.
 */
export function GiftList({ gifts, detailed = false, currency, className = '' }) {
  if (!gifts?.length) return null;
  return (
    <ul className={`space-y-2 ${className}`}>
      {gifts.map((g) => (
        <li key={g.key ?? g.name}>
          <span className="font-medium text-ink">{g.name}</span>
          {detailed && g.description && (
            <span className="block max-w-2xl text-sm text-ink-muted">{g.description}</span>
          )}
          <span className="block text-xs text-ink-muted">
            {GIFT_FULFILMENT_LABELS[g.fulfilment] ?? g.fulfilment}
            {g.retailValueCents != null &&
              ` · ${formatCents(g.retailValueCents, { dropZeroCents: true, currency })} retail value (brand-stated)`}
            {g.limited && (
              <span className="font-medium text-caution">
                {' '}
                · Limited — {g.remaining ?? 0} left
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function TermsGrid({ program, className = '' }) {
  const isGiftOnly = program.commissionType === 'gift_only';
  const gifts = program.gifts ?? [];

  const rows = [
    {
      label: 'Commission',
      value: describeRate(program),
      detail: COMMISSION_TYPE_LABELS[program.commissionType],
      emphasis: true,
    },
    // A gift-only program has nothing to recur and nothing to pay out, so those
    // cells are left out rather than shown as an empty "One-time".
    !isGiftOnly && {
      label: 'Recurring',
      // Given its own cell rather than buried in the rate, because it is the
      // difference between a one-off payment and a year of them.
      value: isRecurring(program)
        ? `${program.recurringMonths} billing ${program.recurringMonths === 1 ? 'cycle' : 'cycles'}`
        : 'One-time',
      detail: isRecurring(program)
        ? 'You earn on every renewal in this window'
        : 'Paid once per conversion',
      emphasis: isRecurring(program),
    },
    program.endsAt && {
      label: 'Runs until',
      value: formatDay(program.endsAt),
      detail:
        program.autoExtend !== false
          ? 'Renews for another year each time'
          : program.paysAfterEnd
            ? 'Nothing new is credited after this; sales from earlier actions, and renewals, keep paying'
            : 'Nothing is credited or paid after this',
    },
    program.intake?.mode === 'window' && {
      label: 'Applications',
      value: `${formatDay(program.intake.opensAt)} – ${formatDay(program.intake.closesAt)}`,
      detail: 'Creators apply in this window, before the program starts',
    },
    {
      label: 'Cookie window',
      value: `${program.cookieWindowDays} ${program.cookieWindowDays === 1 ? 'day' : 'days'}`,
      detail: 'How long after a click a sale still counts',
    },
    !isGiftOnly && {
      label: 'Payout schedule',
      value: PAYOUT_SCHEDULE_LABELS[program.payoutSchedule] ?? program.payoutSchedule,
      // Spelled out, because "Net 60" is finance shorthand and the person
      // deciding whether they can wait that long may not speak it.
      detail:
        PAYOUT_SCHEDULE_DESCRIPTIONS[program.payoutSchedule] ??
        'When approved commissions are paid',
    },
    {
      label: 'Avg. order value',
      value: program.averageOrderValueCents
        ? formatCents(program.averageOrderValueCents, { currency: program.currency })
        : null,
      detail: 'Reported by the brand',
    },
    gifts.length > 0 && {
      label: gifts.length === 1 ? 'Gift' : 'Gifts',
      node: <GiftList gifts={gifts} currency={program.currency} />,
      wide: true,
    },
  ].filter(Boolean);

  // The hairlines between cells are the grid's gap showing the container's grey,
  // so any cell the last row leaves unfilled would show up as a grey block.
  // Pad it with plain cells instead: to the 4-column row on large screens, and
  // to a pair in the 2-column layout.
  const cells = rows.filter((r) => !r.wide);
  const wide = rows.filter((r) => r.wide);
  const padLg = (4 - (cells.length % 4)) % 4;
  const padSm = cells.length % 2;

  const renderRow = (row) => (
    <div
      key={row.label}
      className={`bg-paper-raised px-4 py-3 ${row.wide ? 'sm:col-span-2 lg:col-span-4' : ''}`}
    >
      <dt className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
        {row.label}
      </dt>
      {row.node ? (
        <dd className="mt-1 text-base">{row.node}</dd>
      ) : (
        <dd
          className={`mt-1 tabular-nums ${
            row.emphasis ? 'text-xl font-semibold text-accent' : 'text-base font-medium text-ink'
          }`}
        >
          {row.value ?? <Unknown reason="The brand hasn’t published this" />}
        </dd>
      )}
      {row.detail && <dd className="mt-0.5 text-xs text-ink-faint">{row.detail}</dd>}
    </div>
  );

  return (
    <dl className={`grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-4 ${className}`}>
      {cells.map(renderRow)}
      {Array.from({ length: padLg }, (_, i) => (
        <div
          key={`pad-${i}`}
          aria-hidden="true"
          className={`${i < padSm ? 'hidden sm:block' : 'hidden lg:block'} bg-paper-raised`}
        />
      ))}
      {wide.map(renderRow)}
    </dl>
  );
}

/**
 * Compact one-line terms, for directory rows and dense tables.
 *
 * Takes the public program (which carries `offerSummary`) or a frozen
 * partnership terms object (which carries `gifts`); either way the gifts ride
 * along with the money rather than being left to a separate cell.
 */
export function TermsInline({ program, className = '' }) {
  const offer = program.offerSummary ?? describeOffer(program, program.gifts);
  return (
    <span className={`inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5 ${className}`}>
      <span className="font-semibold tabular-nums text-ink">{offer}</span>
      <span className="text-xs text-ink-muted">
        {program.cookieWindowDays}d cookie
        {program.commissionType !== 'gift_only' &&
          ` · ${PAYOUT_SCHEDULE_LABELS[program.payoutSchedule]}`}
      </span>
    </span>
  );
}

/**
 * Live performance for a program or partnership.
 *
 * Renders bad numbers as readily as good ones, and renders an em dash — never
 * a flattering 0% — when a denominator is empty. `conversionRatePct` and `epc`
 * arrive as null from the API in exactly that case.
 */
export function PerformanceStats({ performance, currency, className = '' }) {
  const p = performance ?? {};
  const stats = [
    { label: 'Unique clicks', value: formatCount(p.uniqueClicks ?? 0) },
    { label: 'Conversions', value: formatCount(p.conversions ?? 0) },
    {
      label: 'Conversion rate',
      value:
        p.conversionRatePct === null || p.conversionRatePct === undefined ? null : (
          formatPercent(p.conversionRatePct)
        ),
      empty: 'No clicks yet, so there is no rate to report',
    },
    {
      label: 'EPC',
      value: p.epc === null || p.epc === undefined ? null : formatCents(Math.round(p.epc * 100), { currency }),
      empty: 'No clicks yet, so there is nothing to divide by',
    },
  ];

  return (
    <div className={`grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-4 ${className}`}>
      {stats.map((s) => (
        <div key={s.label} className="bg-paper-raised px-4 py-3">
          <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
            {s.label}
          </p>
          <p className="mt-1 text-lg font-medium tabular-nums text-ink">
            {s.value ?? <Unknown reason={s.empty} />}
          </p>
        </div>
      ))}
    </div>
  );
}
